import React, { FormEvent, useEffect, useState } from "react";
import { Form, NavLink, useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from 'dayjs';
import { Alert, Button, Checkbox, Container, FormControlLabel, IconButton, InputLabel, MenuItem, Select } from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from "@mui/x-date-pickers";

import { IBatchLight } from "../../../types/shipment";
import { IDocument } from "../../../types/document";
import { ISelectOption, ISelectOptionWithEntity } from "../../../types/list";
import { IVendorLight } from "../../../types/vendor";

import { ErrorProvider } from "../../../classes/errorProvider";
import { useUserContext } from "../../../contexts/userContext";
import { useListContext } from "../../../contexts/listContext";

import { BatchService } from "../../../services/batchService";
import { CustomerService } from "../../../services/customerService";
import { DocumentService } from "../../../services/documentService";
import { UtilityService } from "../../../services/utilityService";
import { VendorService } from "../../../services/vendorService";

import { Icons } from "../../icons";
import Navigation from "../../header";
import PermissionGate from "../../permissionGate";

import "../../../styles/documentAdd.scss";

export interface IDocumentAddProps {
	entityId?: string;
	entityTypeId?: string;
	allowExpiringDocuments?: boolean;
	hideFormCallback?: (document?: IDocument) => void;
}

const DocumentAdd: React.FunctionComponent<IDocumentAddProps> = ({ entityId, entityTypeId, allowExpiringDocuments = true, hideFormCallback }) => {
	const userContext = useUserContext();
	const listContext = useListContext();
	const navigate = useNavigate();
	if (!hideFormCallback) hideFormCallback = () => { navigate('/document') };

	//For controlling the dropdowns
	const [entityOptions, setEntityOptions] = useState<ISelectOption[]>([]);
	const [documentTypeOptions, setDocumentTypeOptions] = useState<ISelectOptionWithEntity[]>([]);
	const [customerOptions, setCustomerOptions] = useState<ISelectOption[]>([]);
	const [vendorOptions, setVendorOptions] = useState<ISelectOption[]>([]);
	const [batchOptions, setBatchOptions] = useState<ISelectOption[]>([]);

	//For controlling form state
	const [fileName, setFileName] = useState("");
	const [formFields, setFormFields] = useState<IDocument>({
		documentTypeId: '',
		entityId: entityId ? entityId : entityOptions?.length ? entityOptions.at(0).value : '',
		entityTypeId: entityTypeId ? entityTypeId : listContext.entityTypes[0].value,
		hasExpirationDate: false,
		expirationDate: null,
		uploadDate: new Date(),
		id: undefined,
		location: undefined,
		isSensitive: false,
	});
	const [isSaving, setIsSaving] = useState(false);
	const [isLoadingOptions, setIsLoadingOptions] = useState(false);
	const [errorMessages, setErrorMessages] = useState<string[] | undefined>();
	const [successMessage, setSuccessMessage] = useState<string>();

	useEffect(() => {
		setDropdownOptions(formFields.entityTypeId);
	}, [entityTypeId, entityId])

	useEffect(() => {
		let expirationDate: Date | null = null;
		if (formFields.hasExpirationDate) {
			const today = new Date();
			expirationDate = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate(), 0, 0, 0, 0);
		}
		setFormFields({ ...formFields, ["expirationDate"]: expirationDate });
	}, [formFields.hasExpirationDate]);

	useEffect(() => {
		setFormFields({ ...formFields, ["documentTypeId"]: documentTypeOptions?.length > 0 ? documentTypeOptions[0].value : '' })

	}, [documentTypeOptions])

	const getEntityTypeDescription = (entityTypeId: string, capitalizeFirst: boolean = false): string => {
		let description = listContext.entityTypes.filter((item) => { return item.value === entityTypeId ? true : false; })[0]?.name?.toLowerCase() ?? "Entity";
		if (capitalizeFirst) {
			description = description.charAt(0).toUpperCase() + description.slice(1);
		}
		return description;
	}

	const setDropdownOptions = async (entityTypeId: string) => {
		//Filter entities
		if (!entityId) {
			const entityTypeDescription = getEntityTypeDescription(entityTypeId);

			switch (entityTypeDescription) {
				case "batch": {
					getBatchOptions();
					break;
				}
				case "customer": {
					getCustomerOptions();
					break;
				}
				case "employee": {
					setEntityOptions(listContext.employees.filter(o => o.isActive));
					entityId = listContext.employees.filter(o => o.isActive).at(0)?.value ?? '';
					break;
				}
				case "vendor": {
					getVendorOptions();
					break;
				}
				case "transporter": {
					setEntityOptions(listContext.transporters.filter(o => o.isActive));
					entityId = listContext.transporters.filter(o => o.isActive).at(0)?.value ?? '';
					break;
				}
			}
		}

		//Filter document types
		let dto = listContext.documentTypes.filter((item) => {
			if (item.type.toString() == entityTypeId) {
				return true;
			}
			return false;
		}).slice();
		setDocumentTypeOptions(dto);

		// Set all the fields at once as multiple calls to setFormFields() tends to overwrite previous updates in the same method
		setFormFields({ ...formFields, ["entityTypeId"]: entityTypeId, ["entityId"]: entityId, ["documentTypeId"]: dto[0]?.value ?? '', })
	}

	const getVendorOptions = () => {
		if (!vendorOptions || !vendorOptions.length) {
			setIsLoadingOptions(true);
			VendorService.getActiveVendors()
				.then((response) => {
					console.log('VendorService.getActiveVendors: SUCCESS', response);
					const activeVendors = (response.data as IVendorLight[])
						.map<ISelectOption>((vendor) => { return { name: vendor.name, value: vendor.id.toString(), isActive: true } })
						.sort((a, b) => UtilityService.compareStrings(a.name, b.name));
					setVendorOptions(activeVendors);
					setEntityOptions(activeVendors);
					entityId = activeVendors.at(0)?.value ?? '';
					setFormFields((prevFormFields) => { return { ...prevFormFields, ["entityId"]: entityId } });
				})
				.catch((error) => {
					console.log('VendorService.getActiveVendors: ERROR', error);
					setErrorMessages([ErrorProvider.getError(error)]);
				})
				.finally(() => {
					setIsLoadingOptions(false);
				});
		}
		else {
			setEntityOptions(vendorOptions);
			entityId = vendorOptions.at(0)?.value ?? '';
			setFormFields((prevFormFields) => { return { ...prevFormFields, ["entityId"]: entityId } });
		}
	}

	const getCustomerOptions = () => {
		if (!customerOptions || !customerOptions.length) {
			setIsLoadingOptions(true);
			CustomerService.getActiveCustomerOptions()
				.then((response) => {
					console.log('CustomerService.getActiveCustomerOptions: SUCCESS', response);
					const activeCustomers = (response.data as ISelectOption[])
						.map<ISelectOption>((customer) => { return { name: customer.name, value: customer.value, isActive: true } })
						.sort((a, b) => UtilityService.compareStrings(a.name, b.name));
					setCustomerOptions(activeCustomers);
					setEntityOptions(activeCustomers);
					entityId = activeCustomers.at(0)?.value ?? '';
					setFormFields((prevFormFields) => { return { ...prevFormFields, ["entityId"]: entityId } });
				})
				.catch((error) => {
					console.log('CustomerService.getActiveCustomerOptions: ERROR', error);
					setErrorMessages([ErrorProvider.getError(error)]);
				})
				.finally(() => {
					setIsLoadingOptions(false);
				});
		}
		else {
			setEntityOptions(customerOptions);
			entityId = customerOptions.at(0)?.value ?? '';
			setFormFields((prevFormFields) => { return { ...prevFormFields, ["entityId"]: entityId } });
		}
	}

	const getBatchOptions = () => {
		if (!batchOptions || !batchOptions.length) {
			setIsLoadingOptions(true);
			BatchService.getBatchLights(false)
				.then((response) => {
					console.log('BatchService.getBatchLights: SUCCESS', response);
					const activeBatches = (response.data as IBatchLight[])
						.sort((a, b) => UtilityService.compareNumbers(b.id, a.id))
						.map<ISelectOption>((batch) => {
							const monthString = batch.month.substring(0, 4) + "/" + batch.month.substring(4);
							const name = `#${batch.id} - ${monthString} - ${batch.invoiceNumber ?? "Missing Invoice"}`;
							return { name: name, value: batch.id.toString(), isActive: true }
						});

					setBatchOptions(activeBatches);
					setEntityOptions(activeBatches);
					entityId = activeBatches.at(0)?.value ?? '';
					setFormFields((prevFormFields) => { return { ...prevFormFields, ["entityId"]: entityId } });
				})
				.catch((error) => {
					console.log('BatchService.getBatchLights: ERROR', error);
					setErrorMessages([ErrorProvider.getError(error)]);
				})
				.finally(() => {
					setIsLoadingOptions(false);
				});
		}
		else {
			setEntityOptions(batchOptions);
			entityId = batchOptions.at(0)?.value ?? '';
			setFormFields((prevFormFields) => { return { ...prevFormFields, ["entityId"]: entityId } });
		}
	}

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {		
		event.preventDefault();
		setErrorMessages(undefined);
		if (!validateForm()) return false;

		setIsSaving(true);
		DocumentService.addDocument(formFields)
			.then((response) => {
				console.log('DocumentService.addDocument: SUCCESS', response);
				//setSuccessMessage("Your document was successfully uploaded.");
				setFormFields({
					documentTypeId: listContext.documentTypes[0].value,
					entityId: entityId ? entityId : entityOptions?.length ? entityOptions.at(0).value : '',
					entityTypeId: entityTypeId ? entityTypeId : listContext.entityTypes[0].value,
					expirationDate: new Date,
					uploadDate: new Date(),
					id: undefined,
					location: undefined,
				});
				hideFormCallback?.(response.data);
			})
			.catch((error) => {
				console.log('DocumentService.addDocument: ERROR', error);
				setErrorMessages([ErrorProvider.getError(error)]);
			})
			.finally(() => {
				setIsSaving(false);
			});
	}

	const handleEntityTypeChange = (entityTypeId: string) => {
		// Setting entityTypeId in setDopDownOptions() to avoid updating the formFields twice
		setDropdownOptions(entityTypeId);
	}

	const handleExpirationDateChange = (value: Dayjs | null) => {
		if (value) {
			let newFields = formFields;
			newFields.expirationDate = value.toDate();
			setFormFields(newFields);
		}
	}

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			let newFields = formFields;
			newFields.file = event.target.files[0];
			setFileName(event.target.files[0].name);
			setFormFields(newFields);
		}
	}


	// Built in validation is being wonky with the file input so just do it manually
	const validateForm = () => {
		let errors = [];
		console.log(formFields);
		if (formFields.entityTypeId === '') errors.push("Entity Type is required.");
		if (formFields.entityId === '') errors.push(`${getEntityTypeDescription(formFields?.entityTypeId, true)} is required.`);
		if (formFields.documentTypeId === '') errors.push("Document Type is required.");
		if (formFields.hasExpirationDate && !formFields.expirationDate) errors.push("If the document has been marked as able to expire, an expiration date is required.");
		if (!formFields.file) errors.push("A file to upload must be selected.");
		setErrorMessages(errors);
		return !errors.length;
	}

	return (
		<div className="hwi-document-add">
			{!entityId &&
				<Navigation></Navigation>
			}
			<Container maxWidth="lg" className="route-container">
				<PermissionGate permission={userContext.permissions?.canManageDocuments}>
					{!entityId &&
						<section className="management-page-header">
							<div className="title">
								<NavLink to="/document">
									<Icons.GoBack fill="#9399ad" height="24px"></Icons.GoBack>
								</NavLink>
								Add Document
							</div>
						</section>
					}
					{errorMessages?.length > 0 &&
						<Alert className="alert-error"
							severity="error"
							action={<IconButton onClick={() => { setErrorMessages(undefined); }} size='small'>X</IconButton>}>
							{(errorMessages?.length ?? 0) > 0 &&
								<ul className={errorMessages.length == 1 ? 'single' : ''}>
									{errorMessages.map((msg, index) => (
										<li key={index}>{msg}</li>
									))}
								</ul>
							}
						</Alert>
					}
					{successMessage &&
						<Alert className="alert"
							severity="success"
							action={<IconButton onClick={() => { setSuccessMessage(undefined); }} size='small'>X</IconButton>}>
							{successMessage}
						</Alert>
					}

					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<Form onSubmit={(event) => handleSubmit(event)}>
							{!entityId &&
								<>
									<div className="row">
										<InputLabel>Entity Type</InputLabel>
										<Select
											value={formFields.entityTypeId}
											className="entity-type-field"
											onChange={(event) => handleEntityTypeChange(event.target.value)}
										>
											{listContext.entityTypes.map((item, index) => {
												if (item.name != "Shipment") {
													return <MenuItem value={item.value} key={index}>{item.name}</MenuItem>;
												}
											})
											}
										</Select>
									</div>
									<div className="row">
										<InputLabel>{getEntityTypeDescription(formFields?.entityTypeId, true)}{isLoadingOptions ? " (Loading...)" : ""}</InputLabel>
										<Select
											value={formFields.entityId ?? ''}
											className="entity-field"
											onChange={(event) => { setFormFields({ ...formFields, ["entityId"]: event.target.value, }) }}
										>
											{entityOptions.map((item, index) => (
												<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
											))}
										</Select>
									</div>
								</>
							}
							<div className="row">
								<InputLabel>Document Type</InputLabel>
								<Select
									value={formFields.documentTypeId}
									className="document-type-field"
									onChange={(event) => { setFormFields({ ...formFields, ["documentTypeId"]: event.target.value, }) }}
								>
									{documentTypeOptions.map((item, index) => (
										<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
									))}
								</Select>
							</div>
							{userContext.permissions?.canViewSensitiveDocuments &&
								<div className="row">
									<InputLabel>Security</InputLabel>
									<FormControlLabel control={<Checkbox name="isSensitive" checked={formFields.isSensitive} onChange={(event) => { setFormFields({ ...formFields, ["isSensitive"]: event.target.checked, }) }}></Checkbox>} label="This is a sensitive document" />
								</div>
							}
							{allowExpiringDocuments &&
								<div className="row">
									<InputLabel>Expiration</InputLabel>
									<FormControlLabel control={<Checkbox name="hasExpirationDate" checked={formFields.hasExpirationDate} onChange={(event) => { setFormFields({ ...formFields, ["hasExpirationDate"]: event.target.checked, }) }}></Checkbox>} label="This document can expire" />
								</div>
							}
							{formFields.hasExpirationDate &&
								<div className="row">
									<InputLabel>Expiration Date</InputLabel>
									<DatePicker
										className="expiration-date-field"
										value={dayjs(formFields.expirationDate)}
										onChange={(value) => handleExpirationDateChange(value)} />
								</div>
							}
							<div className="row">
								<InputLabel>Upload Document</InputLabel>
								<div className="custom-uploader">
									<InputLabel htmlFor="uploader" className="uploader-label">Choose</InputLabel>
									<input type="text" disabled value={fileName}></input>
								</div>
								<input type="file" id="uploader" name="uploader" className="uploader" onChange={(event) => { handleFileChange(event) }} />
							</div>
							<div className="row controls">
								<Button type="submit" className="save-button" variant="contained" disabled={isSaving}>{isSaving ? "Saving..." : "Save"}</Button>
								<Button className="back-button" variant="outlined" onClick={() => hideFormCallback?.()}>Back to List</Button>
							</div>
						</Form>
					</LocalizationProvider>
				</PermissionGate>
			</Container>
			{/* <Json data={entityId} title="Entity:"></Json> */}
			{/* <Json data={entityTypeId} title="Entity Type:"></Json> */}
			{/* <Json data={formFields} title="Form Fields:"></Json> */}
		</div>
	);
};

export default DocumentAdd;